import Phaser from 'phaser';

const TRAVEL_TIME = 700; // ms for grenade to reach target
const EXPLODE_DELAY = 300; // ms after landing before explosion
const ARC_HEIGHT = 80; // px above the straight line at peak

export class Grenade extends Phaser.GameObjects.Ellipse {
  private fromX: number;
  private fromY: number;
  private toX: number;
  private toY: number;
  private elapsed: number = 0;
  private landed: boolean = false;
  private exploded: boolean = false;
  private waitTimer: number = 0;
  private onExplode: (x: number, y: number) => void;
  private shadow: Phaser.GameObjects.Ellipse;

  constructor(
    scene: Phaser.Scene,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    onExplode: (x: number, y: number) => void
  ) {
    super(scene, fromX, fromY, 10, 10, 0x33aa33);
    this.fromX = fromX;
    this.fromY = fromY;
    this.toX = toX;
    this.toY = toY;
    this.onExplode = onExplode;

    scene.add.existing(this);
    this.setDepth(30);
    this.setStrokeStyle(1, 0x004400);

    // Shadow on the ground
    this.shadow = scene.add.ellipse(fromX, fromY, 8, 5, 0x000000, 0.35).setDepth(29);
  }

  update(delta: number): void {
    if (this.exploded) return;

    if (this.landed) {
      this.waitTimer += delta;
      if (this.waitTimer >= EXPLODE_DELAY) {
        this.triggerExplosion();
      }
      return;
    }

    this.elapsed += delta;
    const t = Math.min(this.elapsed / TRAVEL_TIME, 1);

    // Linear interpolation for ground position (shadow)
    const groundX = Phaser.Math.Linear(this.fromX, this.toX, t);
    const groundY = Phaser.Math.Linear(this.fromY, this.toY, t);

    // Parabolic arc: offset Y by a bell-curve above the path
    const arcOffset = -ARC_HEIGHT * 4 * t * (1 - t);

    this.x = groundX;
    this.y = groundY + arcOffset;

    // Scale grenade as it arcs (simulate perspective)
    const scale = 0.7 + 0.6 * t * (1 - t);
    this.setScale(scale + (1 - scale) * t);

    // Shadow follows ground position
    this.shadow.x = groundX;
    this.shadow.y = groundY;
    // Shadow shrinks as grenade is higher
    const shadowScale = 1 - Math.abs(arcOffset) / (ARC_HEIGHT + 20);
    this.shadow.setScale(Math.max(0.2, shadowScale));

    if (t >= 1) {
      this.landed = true;
      this.x = this.toX;
      this.y = this.toY;
      this.shadow.x = this.toX;
      this.shadow.y = this.toY;
      this.shadow.setScale(1);

      // Small bounce effect
      this.scene.tweens.add({
        targets: this,
        y: this.toY - 10,
        duration: 80,
        ease: 'Power1',
        yoyo: true,
      });
    }
  }

  private triggerExplosion(): void {
    if (this.exploded) return;
    this.exploded = true;
    const ex = this.toX;
    const ey = this.toY;
    this.shadow.destroy();
    this.destroy();
    this.onExplode(ex, ey);
  }
}
